import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, Send } from "lucide-react";
import { COMPANY, SERVICES } from "../../data/site";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initial = {
    name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    audience: "homeowner",
    services: [],
    message: "",
};

export const ContactForm = () => {
    const [form, setForm] = useState(initial);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const toggleService = (id) =>
        setForm((f) => ({
            ...f,
            services: f.services.includes(id)
                ? f.services.filter((s) => s !== id)
                : [...f.services, id],
        }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.phone) {
            toast.error("Please fill in name, email, and phone.");
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(`${API}/quotes`, form);
            setSubmitted(true);
            toast.success("Quote request received — we'll be in touch within one business day.");
            setForm(initial);
        } catch (err) {
            console.error(err);
            toast.error("Couldn't send right now. Please call us at " + COMPANY.phone);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section
            id="quote"
            data-testid="contact-section"
            className="py-24 md:py-32 bg-bone"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
                    {/* Left: copy + contact info */}
                    <div className="lg:col-span-5 reveal">
                        <div className="text-xs tracking-[0.22em] uppercase font-bold text-moss mb-5">
                            <span className="h-px w-10 bg-moss inline-block align-middle mr-3" />
                            Get a Free Quote
                        </div>
                        <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.05]">
                            Tell us about your <span className="text-copper">project.</span>
                        </h2>
                        <p className="mt-5 text-muted_ink leading-relaxed">
                            Homeowner or contractor — share a few details and we&apos;ll come back
                            with a real quote, not a number out of thin air. Most jobs get
                            inspected and quoted within 48 hours.
                        </p>

                        <div className="mt-10 space-y-5">
                            <a
                                href={COMPANY.phoneHref}
                                data-testid="contact-phone-link"
                                className="flex items-start gap-4 group"
                            >
                                <Phone
                                    size={20}
                                    strokeWidth={1.5}
                                    className="text-copper mt-0.5"
                                />
                                <div>
                                    <div className="text-xs uppercase tracking-wider text-muted_ink">
                                        Call
                                    </div>
                                    <div className="font-heading font-bold text-lg group-hover:text-copper transition-colors">
                                        {COMPANY.phone}
                                    </div>
                                </div>
                            </a>
                            <a
                                href={COMPANY.emailHref}
                                data-testid="contact-email-link"
                                className="flex items-start gap-4 group"
                            >
                                <Mail
                                    size={20}
                                    strokeWidth={1.5}
                                    className="text-copper mt-0.5"
                                />
                                <div>
                                    <div className="text-xs uppercase tracking-wider text-muted_ink">
                                        Email
                                    </div>
                                    <div className="font-heading font-bold text-lg group-hover:text-copper transition-colors">
                                        {COMPANY.email}
                                    </div>
                                </div>
                            </a>
                            <div className="flex items-start gap-4">
                                <Clock
                                    size={20}
                                    strokeWidth={1.5}
                                    className="text-copper mt-0.5"
                                />
                                <div>
                                    <div className="text-xs uppercase tracking-wider text-muted_ink">
                                        Hours
                                    </div>
                                    <div className="font-heading font-bold text-lg">
                                        {COMPANY.hours}
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-start gap-4">
                                <MapPin
                                    size={20}
                                    strokeWidth={1.5}
                                    className="text-copper mt-0.5"
                                />
                                <div>
                                    <div className="text-xs uppercase tracking-wider text-muted_ink">
                                        Service Area
                                    </div>
                                    <div className="font-heading font-bold text-lg">
                                        Chicago Southland · NW Indiana
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right: Form */}
                    <div className="lg:col-span-7 reveal">
                        {submitted ? (
                            <div
                                data-testid="quote-success-card"
                                className="bg-moss text-bone p-10 md:p-14"
                            >
                                <div className="text-xs uppercase tracking-[0.2em] text-ember font-bold mb-4">
                                    Quote Request Received
                                </div>
                                <h3 className="font-heading text-3xl md:text-4xl font-bold mb-4">
                                    Thank you. We&apos;ll be in touch within one business day.
                                </h3>
                                <p className="text-bone/85 mb-8">
                                    For urgent jobs, call us directly at{" "}
                                    <a
                                        href={COMPANY.phoneHref}
                                        className="underline font-semibold"
                                    >
                                        {COMPANY.phone}
                                    </a>
                                    .
                                </p>
                                <button
                                    onClick={() => setSubmitted(false)}
                                    data-testid="quote-submit-another-btn"
                                    className="btn-lift bg-bone text-ink font-semibold py-3 px-6 hover:bg-ember"
                                >
                                    Submit another
                                </button>
                            </div>
                        ) : (
                            <form
                                data-testid="quote-form"
                                onSubmit={submit}
                                className="bg-white border border-line p-8 md:p-12"
                            >
                                {/* Audience toggle */}
                                <div className="flex gap-2 mb-10 p-1 bg-bone w-fit">
                                    {[
                                        { id: "homeowner", label: "I'm a Homeowner" },
                                        { id: "contractor", label: "I'm a Contractor" },
                                    ].map((opt) => (
                                        <button
                                            key={opt.id}
                                            type="button"
                                            onClick={() => update("audience", opt.id)}
                                            data-testid={`audience-toggle-${opt.id}`}
                                            className={`px-5 py-2.5 text-sm font-semibold transition-colors ${
                                                form.audience === opt.id
                                                    ? "bg-ink text-bone"
                                                    : "text-ink hover:bg-line"
                                            }`}
                                        >
                                            {opt.label}
                                        </button>
                                    ))}
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-7">
                                    <Field
                                        id="name"
                                        label="Full Name"
                                        value={form.name}
                                        onChange={(v) => update("name", v)}
                                        required
                                    />
                                    <Field
                                        id="email"
                                        type="email"
                                        label="Email"
                                        value={form.email}
                                        onChange={(v) => update("email", v)}
                                        required
                                    />
                                    <Field
                                        id="phone"
                                        type="tel"
                                        label="Phone"
                                        value={form.phone}
                                        onChange={(v) => update("phone", v)}
                                        required
                                    />
                                    <Field
                                        id="city"
                                        label="City / Town"
                                        value={form.city}
                                        onChange={(v) => update("city", v)}
                                    />
                                    <div className="md:col-span-2">
                                        <Field
                                            id="address"
                                            label="Property Address (optional)"
                                            value={form.address}
                                            onChange={(v) => update("address", v)}
                                        />
                                    </div>
                                </div>

                                {/* Services chips */}
                                <div className="mt-10">
                                    <label className="text-xs font-bold tracking-wider uppercase text-ink mb-4 block">
                                        Services of interest
                                    </label>
                                    <div className="flex flex-wrap gap-2">
                                        {SERVICES.map((s) => {
                                            const active = form.services.includes(s.id);
                                            return (
                                                <button
                                                    key={s.id}
                                                    type="button"
                                                    onClick={() => toggleService(s.id)}
                                                    data-testid={`service-chip-${s.id}`}
                                                    className={`text-xs uppercase tracking-wider font-bold px-3.5 py-2 border transition-colors ${
                                                        active
                                                            ? "bg-copper text-bone border-copper"
                                                            : "border-line text-ink hover:border-ink"
                                                    }`}
                                                >
                                                    {s.title}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* Message */}
                                <div className="mt-10">
                                    <label
                                        htmlFor="message"
                                        className="text-xs font-bold tracking-wider uppercase text-ink mb-2 block"
                                    >
                                        Tell us about the project
                                    </label>
                                    <textarea
                                        id="message"
                                        data-testid="quote-message-input"
                                        rows={4}
                                        value={form.message}
                                        onChange={(e) => update("message", e.target.value)}
                                        placeholder="Square footage, age of home, current insulation, what's bugging you about it…"
                                        className="w-full border-b-2 border-line bg-transparent py-3 focus:outline-none focus:border-copper transition-colors resize-none"
                                    />
                                </div>

                                <button
                                    type="submit"
                                    disabled={submitting}
                                    data-testid="quote-submit-btn"
                                    className="btn-lift mt-10 inline-flex items-center gap-2 bg-copper text-bone font-semibold py-4 px-8 hover:bg-copper-hover disabled:opacity-60"
                                >
                                    {submitting ? "Sending…" : "Send Quote Request"}
                                    <Send size={16} />
                                </button>
                                <p className="text-xs text-muted_ink mt-5">
                                    We typically reply within one business day. No spam — your
                                    info stays with us.
                                </p>
                            </form>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
};

const Field = ({ id, label, value, onChange, type = "text", required }) => (
    <div>
        <label
            htmlFor={id}
            className="text-xs font-bold tracking-wider uppercase text-ink mb-2 block"
        >
            {label}
            {required && <span className="text-copper ml-1">*</span>}
        </label>
        <input
            id={id}
            data-testid={`quote-${id}-input`}
            type={type}
            required={required}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="w-full border-b-2 border-line bg-transparent py-3 focus:outline-none focus:border-copper transition-colors text-ink placeholder:text-muted_ink"
        />
    </div>
);
